/**
 * EhrBillingSection - "AI billing, built in.", what turning on Altair's AI
 * billing does from inside the EHR. Each line restates a homepage claim
 * (claims checked against the payer's current policy, auths and eligibility
 * before the visit, every denial appealed, every dollar in view, Altair's
 * billers on the exceptions), so change them together.
 *
 * Each line carries the accent of the homepage pillar it belongs to (amber
 * Submit clean, emerald Win the payer game, sky See it all), on ProblemCard's
 * surface. Lines can have a title alone, so they stack in one card rather
 * than in a grid of cards whose heights would not match.
 *
 * Copy rules: payer requirements are "policies", never "rules"; no price,
 * percentage or payment model; the practice is never the subject of a
 * billing verb.
 */

import { acc, type HomeAccent } from "@/components/landing/home/home-accent";
import { useReveal } from "@/components/landing/landing-animations";
import { EhrHeading } from "./EhrHeading";

const INK = "hsl(225 45% 14%)";
const BODY_COLOR = "hsl(225 45% 14% / 0.64)";
const INTRO_COLOR = "hsl(225 45% 14% / 0.66)";
const RULE_COLOR = "hsl(225 45% 14% / 0.1)";
const CARD_BG = "hsl(38 30% 97%)";
const CARD_BORDER = "hsl(225 45% 14% / 0.08)";
const CARD_SHADOW = "0 30px 60px -44px hsl(225 45% 14% / 0.16)";

interface BillingLine {
  accent: HomeAccent;
  title: string;
  body?: string;
}

const LINES: BillingLine[] = [
  {
    accent: "amber",
    title: "Checked before it's sent.",
    body: "Every claim is checked against the payer's current policy before it goes out.",
  },
  { accent: "violet", title: "Prior auths and eligibility, done before the visit." },
  { accent: "emerald", title: "Every denial fought.", body: "Altair appeals every denial for you." },
  { accent: "sky", title: "Every dollar in view.", body: "See every claim and every dollar as it moves." },
  {
    accent: "rose",
    title: "No billing team to manage.",
    body: "Altair's AI does the work, and our in-house billers handle the exceptions.",
  },
];

/** One line of the card. Owns its own reveal ref. */
function BillingRow({ line }: { line: BillingLine }) {
  const ref = useReveal<HTMLLIElement>();
  const ink = acc(line.accent).ink;
  return (
    <li ref={ref} className="landing-reveal ehr-billing-row">
      <span
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          marginTop: 8,
          borderRadius: 999,
          background: ink,
          boxShadow: `0 0 0 4px hsl(var(--accent-${line.accent}-ink) / 0.14)`,
        }}
      />
      <div>
        <h3
          style={{
            margin: 0,
            fontFamily: "'Space Grotesk',sans-serif",
            fontSize: "clamp(18px,1.9vw,21px)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.3,
            color: INK,
            textWrap: "balance",
          }}
        >
          {line.title}
        </h3>
        {line.body && (
          <p style={{ margin: "6px 0 0", fontSize: 16, lineHeight: 1.6, color: BODY_COLOR }}>
            {line.body}
          </p>
        )}
      </div>
    </li>
  );
}

export function EhrBillingSection() {
  const introRef = useReveal<HTMLParagraphElement>();
  const cardRef = useReveal<HTMLDivElement>();

  return (
    <section
      data-screen-label="AI billing"
      className="home-anim ehr-billing-section"
      style={{
        position: "relative",
        padding: "140px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        color: INK,
        borderTop: "1px solid hsl(225 45% 14% / 0.06)",
      }}
    >
      <EhrHeading lead="AI billing," beat="built in." marginBottom={22} />

      <p
        ref={introRef}
        className="landing-reveal"
        style={{
          margin: "0 0 56px",
          textAlign: "center",
          fontSize: "clamp(17px,1.8vw,20px)",
          lineHeight: 1.55,
          color: INTRO_COLOR,
          maxWidth: 540,
          textWrap: "balance",
        }}
      >
        Turn it on and Altair runs your billing from the same place you write
        your notes.
      </p>

      <div
        ref={cardRef}
        className="landing-reveal ehr-billing-card"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 820,
          padding: "6px 44px",
          background: CARD_BG,
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: 22,
          boxShadow: CARD_SHADOW,
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(to right, ${acc("sky").ink}, transparent 72%)`,
          }}
        />
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {LINES.map((line) => (
            <BillingRow key={line.title} line={line} />
          ))}
        </ul>
      </div>

      <style>{`
        .ehr-billing-row {
          display: grid;
          grid-template-columns: 10px minmax(0, 1fr);
          gap: 22px;
          padding: 28px 0;
        }
        .ehr-billing-row + .ehr-billing-row {
          border-top: 1px solid ${RULE_COLOR};
        }
        @media (max-width: 768px) {
          .ehr-billing-section {
            padding-top: 88px !important;
            padding-bottom: 88px !important;
          }
          .ehr-billing-card {
            padding: 2px 22px !important;
          }
          .ehr-billing-row {
            gap: 18px;
            padding: 22px 0;
          }
        }
      `}</style>
    </section>
  );
}
